/**
 * Validates the `formData`/`data` payload of every node and edge in a pasted/imported
 * blueprint against the same Zod schemas the drawer forms use. Catches shape mismatches
 * (e.g. a number pasted where a string is expected) that would otherwise pass the JSON.parse
 * + structural checks and only surface later as a crash deep in the diagram (e.g.
 * `formData.title.trim()` in buildParameterPickerCategories).
 *
 * Missing fields are filled from the node/edge defaults before validation — the goal is to
 * catch wrong types, not to force every pasted blueprint to be already-complete.
 */

import { conditionFormDefaultValues, conditionFormSchema } from "components/CC/Edges/ConditionEdge/Forms/formSchema";
import { BlueprintData } from "components/CC/utils/adaptBlueprint.ts";
import { getDefaultFormData } from "components/CC/utils/nodeDefaults.ts";
import { NODE_FORM_DATA_SCHEMAS } from "components/CC/utils/nodeFormDataSchemas.ts";
import { z } from "zod";

export interface BlueprintFormDataIssue {
    entity: "node" | "edge";
    id: string;
    type?: string;
    path: string;
    /** Raw Zod issue path (e.g. ["data", 0, "fields", 0, "source"]) — used to locate the exact line in the editor. */
    pathSegments: (string | number)[];
    message: string;
}

// The envelope every node/edge must have regardless of type — React Flow itself requires `id`
// and (for nodes) `position` to render at all. Per-type formData validation below assumes this
// already holds, so a node/edge failing this check is rejected outright rather than merged with
// defaults and let through.
const diagramNodeBaseSchema = z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    position: z.object({ x: z.number(), y: z.number() }),
});

const diagramEdgeBaseSchema = z.object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const formatPath = (issuePath: (string | number)[]): string =>
    issuePath.reduce<string>((acc, segment) => {
        if (typeof segment === "number") return `${acc}[${segment}]`;
        return acc ? `${acc}.${segment}` : String(segment);
    }, "");

const collectIssues = (
    entity: "node" | "edge",
    id: string,
    type: string | undefined,
    result: z.SafeParseReturnType<unknown, unknown>
): BlueprintFormDataIssue[] => {
    if (result.success) return [];
    return result.error.issues.map((issue) => ({
        entity,
        id,
        type,
        path: formatPath(issue.path),
        pathSegments: issue.path,
        message: issue.message,
    }));
};

// Mirrors the pattern GeneralFormProvider uses to detect a Zod message that is actually a
// translation key (our custom refinements use full-path keys) vs. Zod's own built-in English
// messages (e.g. "Expected string, received number"), which are shown as-is.
const TRANSLATION_KEY_PATTERN = /^[a-z0-9]+([_-][a-z0-9]+)*(\.[a-z0-9]+([_-][a-z0-9]+)*){2,}$/;

export const translateIssueMessage = (t: (key: string) => string, message: string): string => {
    if (!TRANSLATION_KEY_PATTERN.test(message)) return message;
    const translated = t(message);
    return translated === message ? message : translated;
};

export const formatBlueprintFormDataIssue = (t: (key: string) => string, issue: BlueprintFormDataIssue): string => {
    const message = translateIssueMessage(t, issue.message);
    const location = issue.path ? ` (${issue.path})` : "";
    return issue.entity === "edge"
        ? `${issue.id}${location}: ${message}`
        : `${issue.id} [${issue.type}]${location}: ${message}`;
};

// `formData`/`data` come from parsed JSON, so despite the parameter type they can be anything at
// runtime. Only a true omission (undefined) or a genuine plain object is safe to default-fill —
// a string/number/array/null must be passed through unchanged so Zod's own type check rejects it,
// rather than being silently swallowed by the spread (e.g. {...42} spreads to nothing) and then
// passing validation as an accidental all-defaults object.
const mergeNodeFormData = (nodeType: string, formData: unknown): unknown => {
    if (formData === undefined) return getDefaultFormData(nodeType);
    if (!isRecord(formData)) return formData;
    return { ...(getDefaultFormData(nodeType) as Record<string, unknown>), ...formData };
};

const mergeEdgeData = (data: unknown): unknown => {
    if (data === undefined) return conditionFormDefaultValues;
    if (!isRecord(data)) return data;
    return { ...conditionFormDefaultValues, ...data };
};

/** Same field-level validation as validateBlueprintFormData, for a single pasted node (NodeBlueprintDrawer). */
export function validateNodeFormData(nodeType: string, formData: unknown): BlueprintFormDataIssue[] {
    const schema = NODE_FORM_DATA_SCHEMAS[nodeType];
    if (!schema) return [];

    return collectIssues("node", nodeType, nodeType, schema.safeParse(mergeNodeFormData(nodeType, formData)));
}

/**
 * Fills in any formData/data fields missing from a pasted node/edge with the node's own defaults
 * — the same merge validation already tolerates (it rejects wrong types, not omissions). Without
 * this, a blueprint that passes validation because a field was entirely absent (e.g. an end node
 * missing `success`) would get applied to the diagram with that hole still there, since nothing
 * downstream re-applies the defaults that validation only used in memory.
 */
export function normalizeNodeFormData(nodeType: string, formData: unknown): unknown {
    if (!NODE_FORM_DATA_SCHEMAS[nodeType]) return formData ?? {};
    return mergeNodeFormData(nodeType, formData);
}

export function normalizeBlueprintFormData(blueprint: BlueprintData): BlueprintData {
    return {
        nodes: blueprint.nodes.map((node) => {
            if (!diagramNodeBaseSchema.safeParse(node).success) return node;
            if (!NODE_FORM_DATA_SCHEMAS[node.type]) return node;

            const data = node.data as Record<string, unknown> | undefined;
            return {
                ...node,
                data: {
                    ...data,
                    formData: mergeNodeFormData(node.type, data?.formData),
                },
            };
        }),
        edges: blueprint.edges.map((edge) => {
            if (!diagramEdgeBaseSchema.safeParse(edge).success) return edge;
            if ((edge.type ?? "customEdge") !== "customEdge" || edge.data === undefined) return edge;
            return { ...edge, data: mergeEdgeData(edge.data) as Record<string, unknown> };
        }),
    };
}

export function validateBlueprintFormData(blueprint: BlueprintData): BlueprintFormDataIssue[] {
    const issues: BlueprintFormDataIssue[] = [];

    blueprint.nodes.forEach((node, index) => {
        const label = typeof node?.id === "string" && node.id ? node.id : `nodes[${index}]`;
        const baseResult = diagramNodeBaseSchema.safeParse(node);
        if (!baseResult.success) {
            issues.push(...collectIssues("node", label, node?.type, baseResult));
            return; // formData can't be trusted without a valid envelope (id/type/position)
        }

        const schema = NODE_FORM_DATA_SCHEMAS[node.type];
        if (!schema) return;

        const formData = (node.data as Record<string, unknown> | undefined)?.formData;
        issues.push(
            ...collectIssues("node", node.id, node.type, schema.safeParse(mergeNodeFormData(node.type, formData)))
        );
    });

    blueprint.edges.forEach((edge, index) => {
        const label = typeof edge?.id === "string" && edge.id ? edge.id : `edges[${index}]`;
        const baseResult = diagramEdgeBaseSchema.safeParse(edge);
        if (!baseResult.success) {
            issues.push(...collectIssues("edge", label, edge?.type, baseResult));
            return; // source/target-only checks (findDuplicateConnection) can't help if id is missing too
        }

        if ((edge.type ?? "customEdge") !== "customEdge") return;
        if (edge.data === undefined) return;

        issues.push(
            ...collectIssues("edge", edge.id, edge.type, conditionFormSchema.safeParse(mergeEdgeData(edge.data)))
        );
    });

    return issues;
}
